import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Calendar } from "lucide-react";

function buildMonthOptions(count = 24) {
  const out = [];
  const now = new Date();
  for (let i = 0; i < count; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    const label = d.toLocaleDateString("en-IN", { month: "short", year: "numeric" });
    out.push({ ym, label });
  }
  return out;
}

export function currentMonthYm() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export function humanMonth(ym) {
  if (!ym) return "";
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(y, m - 1, 1);
  return d.toLocaleDateString("en-IN", { month: "long", year: "numeric" });
}

/**
 * MonthPicker — bound to `?month=YYYY-MM` in the URL.
 * If URL has no `month`, defaults to current month.
 */
export default function MonthPicker({ testId = "month-picker" }) {
  const [params, setParams] = useSearchParams();
  const monthOptions = useMemo(() => buildMonthOptions(24), []);
  const month = params.get("month") || currentMonthYm();
  const [customOpen, setCustomOpen] = useState(false);
  const [customMonth, setCustomMonth] = useState("");

  useEffect(() => {
    if (!params.get("month")) {
      const next = new URLSearchParams(params);
      next.set("month", currentMonthYm());
      setParams(next, { replace: true });
    }
  }, [params, setParams]);

  const setMonth = (m) => {
    const next = new URLSearchParams(params);
    next.set("month", m);
    setParams(next);
  };

  return (
    <div className="flex items-center gap-2" data-testid={testId}>
      <Calendar className="h-3.5 w-3.5 text-slate-400" />
      <span className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">
        Month
      </span>
      <select
        data-testid={`${testId}-select`}
        value={monthOptions.some(o => o.ym === month) ? month : "__custom__"}
        onChange={(e) => {
          if (e.target.value === "__custom__") setCustomOpen(true);
          else setMonth(e.target.value);
        }}
        className="text-xs border border-slate-200 rounded px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-red-400"
      >
        {monthOptions.map((o) => (
          <option key={o.ym} value={o.ym}>{o.label}</option>
        ))}
        <option value="__custom__">Custom…</option>
      </select>
      {customOpen && (
        <div className="flex items-center gap-1">
          <input
            type="month"
            data-testid={`${testId}-custom`}
            value={customMonth}
            onChange={(e) => setCustomMonth(e.target.value)}
            className="text-xs border border-slate-200 rounded px-2 py-1 bg-white"
          />
          <button
            type="button"
            onClick={() => {
              if (customMonth) setMonth(customMonth);
              setCustomOpen(false);
            }}
            className="text-xs bg-red-600 text-white px-2 py-1 rounded hover:bg-red-700"
          >
            Apply
          </button>
        </div>
      )}
    </div>
  );
}
